import React, { PropsWithChildren, useMemo } from "react";
import { QuestViewContext } from "../QuestViewContext";
import { selectLoggedInUserId } from "@app/store/auth";
import { useAppSelector } from "@app/store";
import { useChangeTracker } from "@app/quest/edit/useChangeTracker";
import {
  selectEditableQuestPrototypeDetailsById,
  selectQuestPrototypeById,
} from "@app/store/cache/questPrototypes";
import isEqual from "react-fast-compare";
import { sentry } from "@app/util/sentry";

interface PreviewQuestViewContextProviderProps {
  questId: string;
  questPrototypeId: string;
}

export const PreviewQuestViewContextProvider: React.FC<
  PropsWithChildren<PreviewQuestViewContextProviderProps>
> = ({ questId, questPrototypeId, children }) => {
  const questPrototypeFromServer = useAppSelector(
    (state) =>
      selectEditableQuestPrototypeDetailsById(state, questPrototypeId)!,
    isEqual
  );

  const { useValueWithChanges } = useChangeTracker(questPrototypeFromServer);
  const isOwner = useAppSelector((state) => {
    return (
      selectLoggedInUserId(state) ===
      selectQuestPrototypeById(state, questPrototypeId!)?.ownerId
    );
  });

  const staticContext = useMemo(
    // Needed to match fields provided by 'MANAGE' view context
    () => ({
      save: () =>
        Promise.resolve({
          success: true,
          result: "SKIPPED",
          reason: "save called in PREVIEW viewContext",
        } as const),
      isSaving: false,
      errorMap: {},
      touchedMap: {},
      useScopedValidationErrors: () => [],
      setFieldTouched: () => {
        const msg = "setFieldTouched called in PREVIEW viewContext";
        console.warn(msg);
        sentry.captureMessage(msg, {
          contexts: {
            Data: {
              questId,
              questPrototypeId,
            },
          },
        });
      },
      markAllFieldsAsTouched: () => {
        const msg = "markAllFieldsAsTouched called in PREVIEW viewContext";
        console.warn(msg);
        sentry.captureMessage(msg, {
          contexts: {
            Data: {
              questId,
              questPrototypeId,
            },
          },
        });
      },
      addChange: () => {
        const msg = "addChange called in PREVIEW viewContext";
        console.warn(msg);
        sentry.captureMessage(msg, {
          contexts: {
            Data: {
              questId,
              questPrototypeId,
            },
          },
        });
      },
      setValidationContext: () => {
        const msg = "setValidationContext called in PREVIEW viewContext";
        console.warn(msg);
        sentry.captureMessage(msg, {
          contexts: {
            Data: {
              questId,
              questPrototypeId,
            },
          },
        });
      },
      advancedMode: false,
    }),
    [questPrototypeId, questId]
  );

  const context = useMemo(() => {
    return {
      viewContext: "PREVIEW",
      questId,
      questPrototypeId,
      useQuestPrototypeWithChanges: useValueWithChanges,
      isOwner,
      ...staticContext,
    } as const;
  }, [questId, questPrototypeId, useValueWithChanges, isOwner, staticContext]);
  return (
    <QuestViewContext.Provider value={context}>
      {children}
    </QuestViewContext.Provider>
  );
};
